import React, { Fragment, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { below } from 'common/utils/breakpoint';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createSelector } from 'reselect';
import queryString from 'query-string';

import getSectionListData from 'redux/selector/getSectionListData';
import FormList from 'client/components/FormContent/FieldList/FormList';

import FormProgressbar from 'client/components/FormContent/FormProgressbar';
import { selectPage } from 'redux/modules/flag';
import { backgroundType } from 'common/style/cssStyleType';
import { useSelectPageId } from 'common/hooks/useSelectPageId';
import useReference from 'editor/components/FormContent/Field/hooks/useReference';
import { useEffectPostMsgHeight, useEmbededPostMsgInfo } from 'common/hooks/useEmbedPostMsg';
import { isInIframe } from 'api/common';
import useFormStyleSetting from 'common/hooks/useFormStyleSetting';
import { scrollElementIntoView } from 'common/utils/scroll';
import usePrevious from 'common/hooks/usePrevious';
import { getTypeConfig } from 'common/utils/config';
import DialogErrMsg from 'client/components/FormContent/DialogErrMsg';

const reSelectSectionList = createSelector(
    [state => state.pages, state => state.sections, state => state.settings],
    (pages, sections, settings) => getSectionListData(pages, sections, settings)
);

const ignoreFields = [501, 502];

function FormContent({ history, isResult, routeSponsor, isInFrame, sponsorId, refetch }) {
    const { selectPageId, formVersion, id, title, templateType, status } = useSelector(state => {
        return { ...state.flag, ...state.form };
    });
    const options = useSelector(state => state.options);

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [firstErrorFieldId, setFirstErrorFieldId] = useState(0);
    const [errMsg, setErrMsg] = useState();
    const [errorCounter, setErrorCounter] = useState(0);
    const [shouldResetMultiChoice, setShouldResetMultiChoice] = useState(false);
    const prevErrorCounter = usePrevious(errorCounter);
    const prevSelectPageId = usePrevious(selectPageId);
    const sectionListData = useSelector(reSelectSectionList);
    useSelectPageId({ sectionListData, isResult });

    const { formBackground, submitPostMessage, enableRecaptCha } = getTypeConfig(templateType);

    const diabledFormBackground = !formBackground;
    const [formStyleSetting] = useFormStyleSetting();
    const {
        formBg,
        formContentBg,
        formContentTextColor,
        formContentTextBold,
        formContentDescColor,
        formAnswerTextColor,
        formOptionTextColor,
        formOptionBgColor
    } = formStyleSetting;
    const settingStyle = {
        textColor: formContentTextColor,
        textBold: formContentTextBold,
        descColor: formContentDescColor,
        answerColor: formAnswerTextColor,
        optionTextColor: formOptionTextColor,
        optionBgColor: formOptionBgColor
    };
    const { pages, sections, fields } = useSelector(state => state);
    const sortedFieldIds = useMemo(() => {
        const sortedLevel = pages
            .filter(p => p.type === 1)
            .map(({ id, seq }) => {
                const sec = sections
                    .filter(s => s.pageId === id)
                    .map(({ id, seq }) => {
                        const fid = fields.filter(f => f.sectionId === id).map(({ id }) => id);
                        fid.sort((a, b) => a - b);
                        return {
                            id,
                            seq,
                            fieldIds: fid
                        };
                    });
                sec.sort(bySeq);
                return {
                    id,
                    seq,
                    sections: sec
                };
            });
        sortedLevel.sort(bySeq);
        const ids = [];
        sortedLevel.forEach(({ sections }) =>
            sections.forEach(({ fieldIds }) => fieldIds.forEach(fid => ids.push(fid)))
        );
        return ids;
    }, [pages, sections, fields]);
    const sectionList = sectionListData.find(section => section.pageId === selectPageId).sections;
    const fieldDataList = useSelector(state => {
        const data = state.fields
            .map(field => {
                const setting = state.settings.fields.find(setting => setting.id === field.id);
                const type = state.sections.find(section => section.id === field.sectionId).type;
                return ignoreFields.includes(type) ? false : { ...field, ...setting, type };
            })
            .filter(f => f);
        return data;
    });
    const [progress, setProgress] = useState(0);

    const inIframe = isInIframe();
    const { elemRef: formContentRef, measuredRef: setFormContentRef } = useReference(null);
    const { elemRef: progressRef, measuredRef: setProgressRef } = useReference(null);
    useEffectPostMsgHeight(formContentRef, progressRef, inIframe);

    const {
        trackField,
        promoLoginToken,
        serialNo,
        isSuspendPostFormMode
    } = useEmbededPostMsgInfo();
    function handleFormError(errors) {
        const firstErrFieldId = sortedFieldIds.find(fid => Boolean(errors[fid]));
        const { sectionId: firstErrorSectionId } =
            fieldDataList.find(f => f.id === firstErrFieldId) || {};
        const { pageId: firstErrorPageId } =
            sectionListData.find(page => page.sections.some(s => s.id === firstErrorSectionId)) ||
            {};
        if (firstErrorPageId !== selectPageId) {
            dispatch(selectPage(firstErrorPageId));
        }
        setFirstErrorFieldId(firstErrFieldId);
        setErrorCounter(current => current + 1);
    }
    const hiddenFields = useSelector(state => state.hiddenFields);
    const [hiddenFieldsValue, setHiddenFieldsValue] = useState({});
    useEffect(() => {
        const qsObj = queryString.parse(window.location.search);
        const formValues = hiddenFields.reduce((array, { id, name }) => {
            const value = qsObj[name];
            if (value) {
                array.push({ fieldId: id, fieldValue: value });
            }
            return array;
        }, []);
        setHiddenFieldsValue(formValues);
    }, [hiddenFields]);

    useEffect(() => {
        const element = document.getElementById(`section_${firstErrorFieldId}`);
        if (element) {
            scrollElementIntoView(element, 'center');
        }
    }, [firstErrorFieldId, errorCounter]);

    useEffect(() => {
        if (
            prevSelectPageId != null &&
            selectPageId !== prevSelectPageId &&
            prevErrorCounter != null &&
            errorCounter === prevErrorCounter &&
            formContentRef
        ) {
            scrollElementIntoView(formContentRef);
        }
    }, [prevSelectPageId, selectPageId, prevErrorCounter, errorCounter, formContentRef]);

    useEffect(() => {
        if (isResult && formContentRef) {
            scrollElementIntoView(formContentRef);
        }
    }, [isResult, formContentRef]);

    const onAfterResetMultiChoice = useCallback(() => {
        setShouldResetMultiChoice(false);
    }, []);

    const handleRefetch = useCallback(
        async formId => {
            await refetch(formId);
            setShouldResetMultiChoice(true);
        },
        [refetch]
    );

    return (
        <Fragment>
            {Boolean(errMsg) && (
                <DialogErrMsg
                    onClose={() => setErrMsg()}
                    title={errMsg.title}
                    code={errMsg.code}
                    message={errMsg.msg}
                />
            )}
            <div
                css={css(
                    wrapperStyle,
                    !diabledFormBackground &&
                        backgroundType({
                            ...formBg
                        }),
                    inIframe && diabledFormBackground && { overflow: 'hidden' }
                )}
            >
                <div
                    css={css(
                        backgroundType({
                            ...formContentBg
                        }),
                        maskStyles
                    )}
                >
                    <div ref={setFormContentRef} css={css(preventMarginCollapse)}>
                        <div css={css(cssSectionCanvas(diabledFormBackground))}>
                            <FormList
                                t={t}
                                formId={id}
                                sponsorId={sponsorId}
                                title={title}
                                formVersion={formVersion}
                                history={history}
                                sectionList={sectionList}
                                fieldDataList={fieldDataList}
                                setProgress={setProgress}
                                isResult={isResult}
                                settingStyle={settingStyle}
                                trackField={trackField}
                                routeSponsor={routeSponsor}
                                onError={handleFormError}
                                onErrorMsg={setErrMsg}
                                hiddenFieldsValue={hiddenFieldsValue}
                                isInFrame={isInFrame}
                                promoLoginToken={promoLoginToken}
                                promoSerialNo={serialNo}
                                isSuspendPostFormMode={isSuspendPostFormMode}
                                sortedFieldIds={sortedFieldIds}
                                submitPostMessage={submitPostMessage}
                                enableRecaptCha={enableRecaptCha}
                                formStatus={status}
                                options={options}
                                refetch={handleRefetch}
                                shouldResetMultiChoice={shouldResetMultiChoice}
                                onAfterResetMultiChoice={onAfterResetMultiChoice}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {false /*!isResult*/ && <FormProgressbar bindRef={setProgressRef} value={progress} />}
        </Fragment>
    );
}

FormContent.propTypes = {
    isResult: PropTypes.bool,
    history: PropTypes.object,
    routeSponsor: PropTypes.string,
    isInFrame: PropTypes.bool,
    sponsorId: PropTypes.number,
    refetch: PropTypes.func
};

export default FormContent;

const bySeq = (a, b) => a.seq - b.seq;

const cssSectionCanvas = fullWidth => `
    width: ${fullWidth ? '100%' : '720px'};
    margin: ${fullWidth ? '0' : '50px auto'};
    padding: 48px 0 44px 0;
    // box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    // background: #FFFFFF;
    ${below('sm')} {
      width: 100%;
      margin: ${fullWidth ? '0' : '24px auto'};
    }
`;

const wrapperStyle = `
    position: relative;
    height: 100%;
    overflow: auto;
    ${below('xlg')} {
        width: 100%;
    }
`;

const preventMarginCollapse = `
    &:before {
        display: table;
        content: " "
    }
    &:after {
        display: table;
        content: " "
    }
`;

const maskStyles = css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;
